/**
 * Custom hook for listening to Firestore chat messages in real-time
 * 
 * This hook manages the Firestore listener lifecycle and cleans up on unmount
 */

import { useEffect, useState, useRef } from 'react';
import { Message, Participant } from '../types';
import { DamageCaseFirebaseClient } from '../../../utils/damageCaseFirebase';

interface UseChatListenerOptions {
    chatToken: string | number | null;
    firebaseClient: DamageCaseFirebaseClient | null;
    enabled?: boolean;
}

interface UseChatListenerReturn {
    messages: Message[];
    participants: Participant[];
    loading: boolean;
    error: Error | null;
}

/**
 * Hook to listen to Firestore chat document in real-time
 */
export function useChatListener({
    chatToken,
    firebaseClient,
    enabled = true,
}: UseChatListenerOptions): UseChatListenerReturn {
    const [messages, setMessages] = useState<Message[]>([]);
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    
    const unsubscribeRef = useRef<(() => void) | null>(null);
    const currentTokenRef = useRef<string | number | null>(null);

    useEffect(() => {
        // Reset state when token changes
        if (chatToken !== currentTokenRef.current) {
            setMessages([]);
            setParticipants([]);
            setLoading(true);
            setError(null);
            currentTokenRef.current = chatToken;
        }

        // Don't setup listener if disabled or no token
        if (!enabled || !chatToken || !firebaseClient) {
            setLoading(false);
            return;
        }

        const tokenString = String(chatToken);
        const chatRef = firebaseClient.firestore.collection('chats').doc(tokenString);

        setLoading(true);

        // Set up real-time listener
        const unsubscribe = chatRef.onSnapshot(
            (doc) => {
                // Ignore if token changed mid-flight
                if (String(chatToken) !== tokenString) {
                    return;
                }

                try {
                    if (doc.exists) {
                        const chatData = doc.data();
                        const chatMessages = (chatData?.messages || []) as Message[];
                        const chatParticipants = (chatData?.participants || []) as Participant[];

                        // Enrich messages with participant labels if missing
                        const enrichedMessages = chatMessages.map(msg => {
                            if (!msg.name && chatParticipants.length > 0) {
                                const matchedParticipant = chatParticipants.find(
                                    p => p.email.toLowerCase() === msg.email?.toLowerCase()
                                );
                                if (matchedParticipant) {
                                    return {
                                        ...msg,
                                        name: matchedParticipant.label || matchedParticipant.email,
                                    };
                                }
                            }
                            return msg;
                        });

                        setMessages(enrichedMessages);
                        setParticipants(chatParticipants);
                    } else {
                        // Document doesn't exist yet - initialize empty
                        setMessages([]);
                        setParticipants([]);
                    }
                    setError(null);
                } catch (err) {
                    setError(err instanceof Error ? err : new Error('Unknown error'));
                } finally {
                    setLoading(false);
                }
            },
            (err) => {
                setError(err);
                setLoading(false);
            }
        );

        unsubscribeRef.current = unsubscribe;

        // Cleanup function
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [chatToken, enabled, firebaseClient]);

    return { messages, participants, loading, error };
}

